import { getDefaultOptions } from "../services/Category";
import { simpleQuery } from "../services/utils";
import {
  ArrivalRow,
  CapacityRow,
  DepartureRow,
  PageData,
  Status,
  UhCatOption,
  UhCatSearchOption,
} from "./types";

const BASE = "/api/capacite-custom";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(
    new URL(`${BASE}${path}`, window.origin).toString(),
    getDefaultOptions(options, options.body instanceof FormData)
  );
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message ?? `${res.status}: ${res.statusText}`);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export async function fetchUhCats(
  accommodationId: number
): Promise<UhCatOption[]> {
  const data = await request<any[]>(
    `/uh-cats/?hebergement_id=${accommodationId}`
  );
  return data.map((u) => ({
    id: u.id,
    shortName: u.nom_court,
    order: u.rang ?? null,
  }));
}

export async function searchUhCats(
  search: string,
  accommodationId?: number
): Promise<UhCatSearchOption[]> {
  const params = new URLSearchParams({ search });
  if (accommodationId !== undefined) {
    params.set("hebergement_id", String(accommodationId));
  }
  const data = await request<any[]>(`/uh-cats-search/?${params.toString()}`);
  return data.map((u) => ({
    id: u.id,
    shortName: u.nom_court,
    hebergementId: u.hebergement_id,
    hebergementShortName: u.hebergement_nom_court,
  }));
}

function parseArrivalRows(content: any[]): ArrivalRow[] {
  return content
    .filter((row) => row.inEditionScope !== 0)
    .map((row, i) => ({
      _key: `persisted-${i}`,
      arrivalDay: String(row.joutDebutSemaine ?? row.jourDebutSemaine ?? "6"),
      startDate: row.dateDebut ?? "",
      endDate: row.dateFin ?? "",
      inventory: String(row.inventaire ?? ""),
      persisted: true,
    }));
}

function parseDepartureRows(content: any[]): DepartureRow[] {
  return content
    .filter((row) => row.inEditionScope !== 0)
    .map((row, i) => ({
      _key: `persisted-${i}`,
      startDate: row.dateDebut ?? "",
      price: String(row.prix ?? ""),
      pricingConstraint: String(row.contrainteTarifaire ?? ""),
      minStay: String(row.minStay ?? ""),
      possibleArrivalDays: row.joursArriveePossibles
        ? row.joursArriveePossibles.split(",").filter(Boolean)
        : [],
      currentNightlyRate: String(row.prixNuiteeActu ?? ""),
      inEditionScope: row.inEditionScope ?? 1,
      selected: false,
      persisted: true,
    }));
}

function parseCapacityRows(content: any[]): CapacityRow[] {
  return content
    .filter((row) => row.in_edition_scope !== 0)
    .map((row, i) => ({
      _key: `persisted-${i}`,
      openingDate: row.dateOuverture ?? "",
      closingDate: row.dateFermeture ?? "",
      inventory: String(row.inventaire ?? ""),
      inEditionScope: row.in_edition_scope ?? 1,
      persisted: true,
    }));
}

function parseStatuses(content: any[]): {
  statuses: Status[];
  selectedId: number | null;
} {
  const statuses: Status[] = content.map((s) => ({
    pricingStatusId: s.pricingStatusId,
    pricingStatusName: s.pricingStatusName,
    isActive: s.isActive,
  }));
  const active = statuses.find((s) => s.isActive === 1);
  return { statuses, selectedId: active?.pricingStatusId ?? null };
}

export async function fetchCapacityPageData(
  accommodationId: number,
  uhCatId: number,
  year: number
): Promise<PageData> {
  const data = await request<any>("/gestion-arrivees/", {
    method: "POST",
    body: JSON.stringify({
      hebergementId: accommodationId,
      hebergementUhCatId: uhCatId,
      annee: year,
    }),
  });

  const arrivalContent = data.arrivees?.responseContent ?? [];
  const capacityContent = data.capacites?.responseContent ?? [];
  const departureContent = data.departs?.responseContent ?? [];
  const uhCats = (data.hebergement_uh_cats ?? []).map((u: any) => ({
    id: u.id,
    shortName: u.nom_court,
    order: u.rang ?? null,
  }));
  const priceContent = data.price?.responseContent ?? {};
  const statusContent = data.statut?.responseContent ?? [];

  const rawPrice = priceContent.coutAchatUH ?? "";
  const price = rawPrice !== "" && rawPrice !== "0" ? String(rawPrice) : "";

  const departures = parseDepartureRows(departureContent);
  const { statuses, selectedId } = parseStatuses(statusContent);

  return {
    arrivals: parseArrivalRows(arrivalContent),
    capacities: parseCapacityRows(capacityContent),
    departures,
    uhCats,
    price,
    statuses,
    selectedStatusId: selectedId,
  };
}

export async function saveArrivals(
  uhCatId: number,
  year: number,
  rows: ArrivalRow[]
): Promise<{
  departures: DepartureRow[];
  price: string;
  statuses: Status[];
  selectedStatusId: number | null;
}> {
  const data = await request<any>("/update-arrivees/", {
    method: "POST",
    body: JSON.stringify({
      hebergementUhCatId: uhCatId,
      annee: year,
      rows: rows.map((r) => ({
        jourArrivee: r.arrivalDay,
        dateDebut: r.startDate,
        dateFin: r.endDate,
        inventaire: r.inventory,
      })),
    }),
  });

  const departureContent = data.departs?.responseContent ?? [];
  const departures = parseDepartureRows(departureContent);

  const rawPrice = data.coutAchatUH ?? "";
  const price = rawPrice !== "" && rawPrice !== "0" ? String(rawPrice) : "";

  const { statuses, selectedId } = parseStatuses(data.pricingStatus ?? []);

  return {
    departures,
    price,
    statuses,
    selectedStatusId: selectedId,
  };
}

export async function saveDepartures(
  uhCatId: number,
  year: number,
  rows: DepartureRow[]
): Promise<{ statuses: Status[]; selectedStatusId: number | null }> {
  const data = await request<any>("/update-departs/", {
    method: "POST",
    body: JSON.stringify({
      hebergementUhCatId: uhCatId,
      annee: year,
      rows: rows.map((r) => ({
        dateDebut: r.startDate,
        prix: r.price,
        contrainteTarifaire: r.pricingConstraint,
        minStay: r.minStay,
        joursArriveePossibles: r.possibleArrivalDays,
        prixNuiteeActu: r.currentNightlyRate,
      })),
    }),
  });

  const pricingStatusData = data?.content?.pricingStatus?.pricingStatus ?? [];
  const { statuses, selectedId } = parseStatuses(pricingStatusData);
  return { statuses, selectedStatusId: selectedId };
}

// Starts the cpgscom YieldAcquisitionTask custom report (set 1): CSV to the
// Campings.com SFTP + CSV/xlsx by mail. Same as the File transfer page button.
export async function exportPrices(): Promise<{ task_id: number }> {
  return simpleQuery("/api/custom-reports/start-export/", {
    method: "POST",
    body: JSON.stringify({ set_nb: 1 }),
  });
}

export async function savePriceStatus(
  uhCatId: number,
  year: number,
  price: string,
  statusId: number | null
): Promise<{ message: string; content: any }> {
  return request("/edit-price-status/", {
    method: "POST",
    body: JSON.stringify({
      hebergementUhCatId: uhCatId,
      annee: year,
      price,
      statutId: statusId,
    }),
  });
}

export async function saveCapacities(
  uhCatId: number,
  year: number,
  rows: CapacityRow[],
  weekStartDays: string
): Promise<{ message: string }> {
  return request("/update-capacites/", {
    method: "POST",
    body: JSON.stringify({
      hebergementUhCatId: uhCatId,
      annee: year,
      joursDebutSemaine: weekStartDays,
      rows: rows.map((r) => ({
        dateOuverture: r.openingDate,
        dateFermeture: r.closingDate,
        inventaire: r.inventory,
      })),
    }),
  });
}

export async function sortUhCats(
  ids: number[],
  accommodationId: number
): Promise<void> {
  await request("/sort-uhcats/", {
    method: "POST",
    body: JSON.stringify({ ids, hebergementId: accommodationId }),
  });
}
